import React, { useState, useContext, useEffect, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAddressCard } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useParams } from "react-router-dom";

import { DataContext } from "../../../../context/DataContext";
import { getRuta, simpleMessage } from "../../../../helpers/Helpers";
import { tienePermiso } from "../../../../helpers/permisos";
import { getToken } from "../../../../api/Account";
import { cerrarNominaAsync, getNominaByIdAsync } from "../../../../api/Employees";
import { formatearFecha, formatearMonto } from "../../../../helpers/formato";
import {
  CargandoTabla,
  ConfirmDialog,
  EstadoChip,
  EstadoError,
  EstadoVacio,
  KpiCard,
  PageHeader,
  useAviso,
} from "../../../../components/base";
import {
  ERROR_OTRA_SESION,
  celdaFecha,
  celdaMonto,
  encabezadoTabla,
  estadoDeNomina,
  nominaAbierta,
  nominaCerrada,
} from "../comunes";

// Suma de una columna de la respuesta, tal como viene (sin fórmulas nuevas).
const sumar = (filas, campo) => filas.reduce((total, fila) => total + (fila[campo] ?? 0), 0);

// Las columnas de deducciones que trae cada fila de la respuesta.
const CAMPOS_DEDUCCION = [
  "aporteINSS",
  "adelanto",
  "prestamo",
  "otrasDeducciones",
  "montoPermisos",
  "montoReposos",
  "montoSubcidio",
];

// Margen, en centavos, para considerar que dos cifras coinciden. Las columnas son decimal(18,2):
// un centavo de redondeo es legítimo, y comparar en córdobas con decimales de JS lo marcaría mal
// (la resta puede dar 0.010000000000218). Se compara en centavos enteros.
const MARGEN_CENTAVOS = 5;

const NominaDetails = () => {
  const [dataNomina, setDataNomina] = useState(null);
  const [detalleNomina, setDetalleNomina] = useState([]);

  const [localReload, setLocalReload] = useState(false);

  // Estado local de la pantalla: esqueleto mientras carga y error con "Reintentar".
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const peticionActual = useRef(0);
  // Si ya hay datos en pantalla, una recarga fallida no los reemplaza por un error: se avisa.
  const hayDatos = useRef(false);

  // Confirmación de "Cerrar nómina". `cerrando` mantiene el diálogo en "cargando" mientras dura.
  const [confirmandoCierre, setConfirmandoCierre] = useState(false);
  const [cerrando, setCerrando] = useState(false);

  let ruta = getRuta();
  const token = getToken();
  let navigate = useNavigate();
  const { setIsLoading, setIsUnautorized, access } = useContext(DataContext);
  const aviso = useAviso();

  const { id } = useParams();

  const getNomina = async () => {
    const peticion = ++peticionActual.current;
    setCargando(true);
    setError(false);
    setIsLoading(true);
    try {
      const result = await getNominaByIdAsync(token, id);
      if (peticion !== peticionActual.current) return;

      if (!result.statusResponse) {
        setIsLoading(false);
        setCargando(false);
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        if (result.error === ERROR_OTRA_SESION) {
          simpleMessage(result.error, "error");
        }
        if (hayDatos.current) {
          aviso.error("No se pudo actualizar la nómina. Recarga la página para ver su estado.");
        } else {
          setError(true);
        }
        return;
      }

      const nomina = result.data.result;
      if (!nomina) {
        // El servidor respondió bien pero no hay nómina con ese número.
        setIsLoading(false);
        setCargando(false);
        if (hayDatos.current) {
          aviso.error("No se pudo actualizar la nómina. Recarga la página para ver su estado.");
        } else {
          setError("noexiste");
        }
        return;
      }

      hayDatos.current = true;
      setDataNomina(nomina);
      setDetalleNomina(nomina.detalleNominas ?? []);
      setIsLoading(false);
      setCargando(false);
    } catch (e) {
      // Sin red, `errorResponse` lanza una excepción: ni esqueleto eterno ni overlay encendido.
      if (peticion !== peticionActual.current) return;
      setIsLoading(false);
      setCargando(false);
      if (hayDatos.current) {
        aviso.error("No se pudo actualizar la nómina. Recarga la página para ver su estado.");
      } else {
        setError("conexion");
      }
    }
  };

  useEffect(() => {
    getNomina();
  }, [localReload]);

  // Misma función de siempre: una sola llamada a CerrarNomina por confirmación.
  const cerrarNomina = async () => {
    setIsLoading(true);
    try {
      const result = await cerrarNominaAsync(token, id);
      if (!result.statusResponse) {
        setIsLoading(false);
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        if (result.error === ERROR_OTRA_SESION) {
          simpleMessage(result.error, "error");
          return;
        }
        aviso.error(result.error || "No se pudo cerrar la nómina");
        return;
      }
      setIsLoading(false);
      setLocalReload(!localReload);

      aviso.exito("Nómina cerrada");
    } catch (e) {
      setIsLoading(false);
      aviso.error("No se pudo conectar con el servidor. Inténtalo de nuevo.");
    }
  };

  const confirmarCierre = async () => {
    setCerrando(true);
    try {
      await cerrarNomina();
    } finally {
      setCerrando(false);
      setConfirmandoCierre(false);
    }
  };

  // Cada acción pide el permiso que exige su endpoint (EmployeesController): ver el recibo es
  // GetNominaDetail (NOMINA VER) y cerrar la nómina es CerrarNomina (NOMINA UPDATE).
  const puedeAbrir = tienePermiso(access, "NOMINA VER");
  const puedeCerrar = tienePermiso(access, "NOMINA UPDATE");

  const reintentar = getNomina;

  if (cargando && !dataNomina) {
    return (
      <Container maxWidth={false} disableGutters>
        <PageHeader titulo="Nómina" icono={<FontAwesomeIcon icon={faAddressCard} />} />
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <CargandoTabla filas={5} columnas={5} etiqueta="Cargando la nómina…" />
        </Paper>
      </Container>
    );
  }

  if (!dataNomina) {
    return (
      <Container maxWidth={false} disableGutters>
        <Button
          onClick={() => navigate(`${ruta}/nomina`)}
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 1 }}
        >
          Volver a nóminas
        </Button>
        <PageHeader titulo="Nómina" icono={<FontAwesomeIcon icon={faAddressCard} />} />
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          {error === "noexiste" ? (
            <EstadoVacio
              titulo="No se encontró la nómina"
              descripcion="Es posible que el número sea incorrecto. Vuelve a la lista para elegir una."
            />
          ) : (
            <EstadoError
              titulo={
                error === "conexion"
                  ? "No se pudo conectar con el servidor"
                  : "No se pudo cargar la nómina"
              }
              onReintentar={reintentar}
            />
          )}
        </Paper>
      </Container>
    );
  }

  const abierta = nominaAbierta(dataNomina);
  const cerrada = nominaCerrada(dataNomina);

  const totalDevengado = sumar(detalleNomina, "totalDevengado");
  const totalPercibir = sumar(detalleNomina, "totalPercibir");
  const totalDeducciones = CAMPOS_DEDUCCION.reduce(
    (total, campo) => total + sumar(detalleNomina, campo),
    0
  );
  // Si el devengado menos las deducciones de alguna fila no da su neto, no se muestra un total
  // de deducciones que no cuadra: solo las sumas por columna de lo que sí trae la respuesta.
  const cuadra = detalleNomina.every((fila) => {
    const diferencia =
      (fila.totalDevengado ?? 0) -
      CAMPOS_DEDUCCION.reduce((total, campo) => total + (fila[campo] ?? 0), 0) -
      (fila.totalPercibir ?? 0);
    return Math.abs(Math.round(diferencia * 100)) <= MARGEN_CENTAVOS;
  });

  const periodo = `Del ${formatearFecha(dataNomina.desde)} al ${formatearFecha(dataNomina.hasta)}`;
  const fechaPago = cerrada ? formatearFecha(dataNomina.fechaPago) : "pendiente";

  const acciones = (
    <>
      <EstadoChip {...estadoDeNomina(dataNomina.estado)} />
      {abierta ? (
        puedeCerrar ? (
          <Button
            variant="contained"
            startIcon={<PowerSettingsNewIcon />}
            onClick={() => setConfirmandoCierre(true)}
            disabled={cerrando}
          >
            Cerrar nómina
          </Button>
        ) : null
      ) : (
        <Button
          variant="outlined"
          startIcon={<PrintOutlinedIcon />}
          onClick={() => window.open(`${ruta}/printNomina/${id}`)}
        >
          Imprimir nómina
        </Button>
      )}
    </>
  );

  return (
    <Container maxWidth={false} disableGutters>
      <Button
        onClick={() => {
          navigate(`${ruta}/nomina`);
        }}
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 1 }}
      >
        Volver a nóminas
      </Button>

      <PageHeader
        titulo={dataNomina.description}
        subtitulo={`${periodo} · Fecha de pago: ${fechaPago}`}
        icono={<FontAwesomeIcon icon={faAddressCard} />}
        acciones={acciones}
      />

      {detalleNomina.length > 0 ? (
        <Box
          sx={{
            display: "grid",
            gap: 2,
            mb: 3,
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              sm: cuadra ? "repeat(3, minmax(0, 1fr))" : "repeat(2, minmax(0, 1fr))",
            },
          }}
        >
          <KpiCard
            titulo="Devengado"
            valor={totalDevengado}
            formato="monto"
            icono={<PaidOutlinedIcon />}
            tono="primary"
            detalle="Salarios del período, antes de deducciones"
          />
          {cuadra ? (
            <KpiCard
              titulo="Deducciones"
              valor={totalDeducciones}
              formato="monto"
              icono={<RemoveCircleOutlineIcon />}
              tono="warning"
              detalle="INSS, adelantos, préstamos, ausencias y otras"
            />
          ) : null}
          <KpiCard
            titulo="Neto a recibir"
            valor={totalPercibir}
            formato="monto"
            icono={<AccountBalanceWalletOutlinedIcon />}
            tono="success"
            detalle={`${detalleNomina.length} ${
              detalleNomina.length === 1 ? "empleado" : "empleados"
            }`}
          />
        </Box>
      ) : null}

      {detalleNomina.length > 0 && !cuadra ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          En algunos empleados el devengado menos las deducciones no coincide con el neto, así
          que se muestran solo las sumas de cada columna.
        </Alert>
      ) : null}

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography
          component="h2"
          variant="h6"
          sx={{ fontWeight: 700, color: "primary.main", mb: 1 }}
        >
          Empleados de la nómina
        </Typography>

        {detalleNomina.length === 0 ? (
          <EstadoVacio
            titulo="Esta nómina no tiene empleados"
            descripcion="Aquí aparecen los empleados activos con su pago del período."
          />
        ) : (
          <TableContainer>
            <Table
              aria-label="Empleados de la nómina"
              sx={{ "& .MuiTableCell-root": { px: { xs: 1, sm: 2 } } }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ ...encabezadoTabla, display: { xs: "none", sm: "table-cell" } }}
                  >
                    #
                  </TableCell>
                  <TableCell sx={encabezadoTabla}>Empleado</TableCell>
                  <TableCell
                    sx={{ ...encabezadoTabla, display: { xs: "none", md: "table-cell" } }}
                  >
                    Desde
                  </TableCell>
                  <TableCell
                    sx={{ ...encabezadoTabla, display: { xs: "none", md: "table-cell" } }}
                  >
                    Hasta
                  </TableCell>
                  <TableCell
                    sx={{
                      ...encabezadoTabla,
                      textAlign: "right",
                      display: { xs: "none", sm: "table-cell" },
                    }}
                  >
                    Devengado
                  </TableCell>
                  <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>
                    Neto a recibir
                  </TableCell>
                  {puedeAbrir ? (
                    <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>
                      Acciones
                    </TableCell>
                  ) : null}
                </TableRow>
              </TableHead>

              <TableBody>
                {detalleNomina.map((row) => {
                  const { empleado, desde, hasta, totalDevengado, totalPercibir } = row;
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{
                          fontVariantNumeric: "tabular-nums",
                          display: { xs: "none", sm: "table-cell" },
                        }}
                      >
                        {row.id}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {empleado.fullName}
                        </Typography>
                        {/* En pantallas pequeñas el devengado y las fechas pasan debajo del nombre. */}
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            display: { xs: "block", md: "none" },
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {`${formatearFecha(desde)} – ${formatearFecha(hasta)}`}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            display: { xs: "block", sm: "none" },
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {`Devengado ${formatearMonto(totalDevengado)}`}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{ ...celdaFecha, display: { xs: "none", md: "table-cell" } }}
                      >
                        {formatearFecha(desde)}
                      </TableCell>
                      <TableCell
                        sx={{ ...celdaFecha, display: { xs: "none", md: "table-cell" } }}
                      >
                        {formatearFecha(hasta)}
                      </TableCell>
                      <TableCell
                        sx={{ ...celdaMonto, display: { xs: "none", sm: "table-cell" } }}
                      >
                        {formatearMonto(totalDevengado)}
                      </TableCell>
                      <TableCell sx={celdaMonto}>
                        <Typography
                          variant="monto"
                          component="span"
                          sx={{ fontSize: "inherit" }}
                        >
                          {formatearMonto(totalPercibir)}
                        </Typography>
                      </TableCell>
                      {puedeAbrir ? (
                        <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                          <Tooltip title="Ver recibo">
                            <IconButton
                              aria-label={`Ver el recibo de ${empleado.fullName}`}
                              onClick={() => {
                                navigate(`${ruta}/nominadetails/${id}/${row.id}`);
                              }}
                              sx={{ color: "primary.main" }}
                            >
                              <VisibilityOutlinedIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  );
                })}
              </TableBody>

              <TableFooter>
                <TableRow>
                  <TableCell
                    sx={{ display: { xs: "none", sm: "table-cell" } }}
                    aria-hidden="true"
                  />
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "text.primary",
                      fontSize: "0.875rem",
                    }}
                  >
                    Total
                  </TableCell>
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" } }} aria-hidden="true" />
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" } }} aria-hidden="true" />
                  <TableCell
                    sx={{
                      ...celdaMonto,
                      fontWeight: 700,
                      color: "text.primary",
                      fontSize: "0.875rem",
                      display: { xs: "none", sm: "table-cell" },
                    }}
                  >
                    {formatearMonto(totalDevengado)}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...celdaMonto,
                      fontWeight: 700,
                      color: "text.primary",
                      fontSize: "0.875rem",
                    }}
                  >
                    {formatearMonto(totalPercibir)}
                  </TableCell>
                  {puedeAbrir ? <TableCell aria-hidden="true" /> : null}
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <ConfirmDialog
        abierto={confirmandoCierre}
        peligro
        cargando={cerrando}
        titulo="Cerrar la nómina"
        mensaje={
          <>
            <Typography component="p" sx={{ mb: 1 }}>
              {`Se cerrará «${dataNomina.description}» (${periodo.toLowerCase()}).`}
            </Typography>
            <Typography component="p" sx={{ mb: 1 }}>
              Al cerrarla se crea la nómina siguiente con los empleados activos y se registran
              como abonos las cuotas de préstamo de este período.
            </Typography>
            <Typography component="p" sx={{ color: "text.secondary" }}>
              Esta acción no se puede deshacer.
            </Typography>
          </>
        }
        textoConfirmar="Cerrar nómina"
        onConfirmar={confirmarCierre}
        onCancelar={() => setConfirmandoCierre(false)}
      />
    </Container>
  );
};

export default NominaDetails;
